import { useEffect, useState, useCallback } from "react";
import { Loader2, Inbox, Mail, Phone, Briefcase, Calendar, Trash2, MapPin, Target, Tag } from "lucide-react";
import { toast } from "sonner";

import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
} from "@/components/ui/sheet";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const statusBadge = (s) => {
    if (s === "new") return <Badge className="bg-slate-950 text-white">New</Badge>;
    if (s === "contacted")
        return (
            <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                Contacted
            </Badge>
        );
    return (
        <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
            Closed
        </Badge>
    );
};

const INTENT_STYLES = {
    audit:    { bg: "#7C6BFF", color: "#ffffff", label: "audit" },
    ordering: { bg: "#2DE2C6", color: "#083344", label: "ordering" },
    booking:  { bg: "#F59E0B", color: "#ffffff", label: "booking" },
    _default: { bg: "#94A3B8", color: "#ffffff", label: "direct" },
};

function IntentPill({ intent }) {
    const key = (intent || "").toString().toLowerCase();
    const s = INTENT_STYLES[key] || INTENT_STYLES._default;
    return (
        <span
            data-testid={`intent-pill-${s.label}`}
            style={{
                display: "inline-block",
                background: s.bg,
                color: s.color,
                borderRadius: 9999,
                padding: "4px 10px",
                fontSize: 11,
                fontWeight: 500,
                lineHeight: 1.4,
                letterSpacing: 0.2,
            }}
        >
            {s.label}
        </span>
    );
}

function formatReceived(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString("en-GB", {
            day: "numeric",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch (_e) {
        return iso;
    }
}

export default function EnquiriesPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState(null);
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState("");
    const [deleteTarget, setDeleteTarget] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/enquiries");
            setItems(data);
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const open = (e) => setActive({ ...e });
    const close = () => setActive(null);

    const save = async () => {
        if (!active) return;
        setSaving(true);
        try {
            await api.patch(`/admin/enquiries/${active.id}`, {
                status: active.status,
                admin_notes: active.admin_notes || "",
            });
            toast.success("Enquiry updated");
            close();
            load();
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!deleteTarget) return;
        try {
            await api.delete(`/admin/enquiries/${deleteTarget.id}`);
            toast.success("Deleted");
            setDeleteTarget(null);
            load();
        } catch (err) {
            toast.error(formatError(err));
        }
    };

    const filtered = items.filter((e) => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        return (
            e.full_name?.toLowerCase().includes(q) ||
            e.email?.toLowerCase().includes(q) ||
            e.contact_value?.toLowerCase().includes(q) ||
            e.business_name?.toLowerCase().includes(q) ||
            e.town_city?.toLowerCase().includes(q) ||
            e.what_do_you_need?.toLowerCase().includes(q) ||
            e.service_needed?.toLowerCase().includes(q)
        );
    });

    return (
        <div className="space-y-6" data-testid="enquiries-page">
            <header className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="label-eyebrow">Inbox</div>
                    <h1 className="font-display text-3xl lg:text-4xl font-black tracking-tight text-slate-950 mt-1">
                        Enquiries
                    </h1>
                    <p className="text-sm text-slate-500 mt-2 max-w-xl">
                        Every contact-form submission from your public website lands here.
                    </p>
                </div>
            </header>

            <Input
                placeholder="Search by name, business, town, contact, or need..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="max-w-md"
                data-testid="enquiries-search"
            />

            <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
                {loading ? (
                    <div className="p-12 grid place-items-center text-slate-400">
                        <Loader2 className="w-6 h-6 animate-spin" />
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="p-12 text-center" data-testid="enquiries-empty">
                        <div className="w-12 h-12 rounded-full bg-slate-100 grid place-items-center mx-auto">
                            <Inbox className="w-5 h-5 text-slate-400" />
                        </div>
                        <p className="text-sm text-slate-500 mt-3">
                            No enquiries yet. Submissions from your contact form will show up here in real time.
                        </p>
                    </div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-slate-50">
                                <TableHead>Received</TableHead>
                                <TableHead>Name</TableHead>
                                <TableHead>Business</TableHead>
                                <TableHead>Town/city</TableHead>
                                <TableHead>Contact</TableHead>
                                <TableHead>Need</TableHead>
                                <TableHead>Intent</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filtered.map((e) => {
                                const contact = e.contact_value || e.email || e.phone || "—";
                                const need = e.what_do_you_need || e.service_needed || "—";
                                return (
                                    <TableRow
                                        key={e.id}
                                        className="cursor-pointer"
                                        onClick={() => open(e)}
                                        data-testid={`enquiry-row-${e.id}`}
                                    >
                                        <TableCell className="text-xs text-slate-500 whitespace-nowrap">
                                            {formatReceived(e.created_at)}
                                        </TableCell>
                                        <TableCell className="font-semibold text-slate-950">
                                            {e.full_name}
                                        </TableCell>
                                        <TableCell>{e.business_name || "—"}</TableCell>
                                        <TableCell className="text-slate-600">{e.town_city || "—"}</TableCell>
                                        <TableCell className="text-slate-600">{contact}</TableCell>
                                        <TableCell className="text-slate-600">{need}</TableCell>
                                        <TableCell>
                                            <IntentPill intent={e.intent} />
                                        </TableCell>
                                        <TableCell>{statusBadge(e.status)}</TableCell>
                                        <TableCell className="text-right">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={(ev) => {
                                                    ev.stopPropagation();
                                                    setDeleteTarget(e);
                                                }}
                                                className="text-destructive hover:text-destructive"
                                                data-testid={`enquiry-delete-${e.id}`}
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                )}
            </div>

            <Sheet open={!!active} onOpenChange={(o) => !o && close()}>
                <SheetContent className="sm:max-w-xl overflow-y-auto">
                    {active && (
                        <>
                            <SheetHeader>
                                <SheetTitle className="flex items-center gap-2 flex-wrap">
                                    {active.full_name}
                                    <IntentPill intent={active.intent} />
                                </SheetTitle>
                                <SheetDescription>
                                    Enquiry received{" "}
                                    {active.created_at ? formatReceived(active.created_at) : ""}
                                </SheetDescription>
                            </SheetHeader>

                            <div className="space-y-5 py-6">
                                <dl className="grid grid-cols-1 gap-4">
                                    <Row icon={Briefcase} label="Business" value={active.business_name || "—"} />
                                    <Row icon={MapPin} label="Town/city" value={active.town_city || "—"} />
                                    <Row
                                        icon={Mail}
                                        label="Contact"
                                        value={active.contact_value || active.email || active.phone || "—"}
                                    />
                                    {active.contact_value && active.email && active.email !== active.contact_value && (
                                        <Row icon={Mail} label="Email (legacy)" value={active.email} />
                                    )}
                                    {active.phone && active.phone !== active.contact_value && (
                                        <Row icon={Phone} label="Phone (legacy)" value={active.phone} />
                                    )}
                                    <Row
                                        icon={Target}
                                        label="What they need"
                                        value={active.what_do_you_need || "—"}
                                    />
                                    {active.service_needed && active.service_needed !== active.what_do_you_need && (
                                        <Row icon={Calendar} label="Service (legacy)" value={active.service_needed} />
                                    )}
                                    {active.budget_range && (
                                        <Row icon={Calendar} label="Budget range" value={active.budget_range} />
                                    )}
                                    <Row
                                        icon={Tag}
                                        label="Intent (funnel source)"
                                        value={active.intent || "direct"}
                                    />
                                </dl>

                                {active.project_details ? (
                                    <div>
                                        <div className="label-eyebrow mb-2">Project details</div>
                                        <p className="text-sm text-slate-700 whitespace-pre-line bg-slate-50 border border-slate-200 rounded-md p-3">
                                            {active.project_details}
                                        </p>
                                    </div>
                                ) : null}

                                <div className="space-y-2">
                                    <div className="label-eyebrow">Status</div>
                                    <Select
                                        value={active.status}
                                        onValueChange={(v) => setActive({ ...active, status: v })}
                                    >
                                        <SelectTrigger data-testid="enquiry-status-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="new">New</SelectItem>
                                            <SelectItem value="contacted">Contacted</SelectItem>
                                            <SelectItem value="closed">Closed</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2">
                                    <div className="label-eyebrow">Admin notes</div>
                                    <Textarea
                                        rows={5}
                                        value={active.admin_notes || ""}
                                        onChange={(ev) =>
                                            setActive({ ...active, admin_notes: ev.target.value })
                                        }
                                        placeholder="Internal notes — only visible to admins"
                                        data-testid="enquiry-notes-textarea"
                                    />
                                </div>

                                <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
                                    <Button variant="ghost" onClick={close}>
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={save}
                                        disabled={saving}
                                        className="bg-slate-950 hover:bg-slate-800 text-white"
                                        data-testid="enquiry-save-button"
                                    >
                                        {saving ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            "Save changes"
                                        )}
                                    </Button>
                                </div>
                            </div>
                        </>
                    )}
                </SheetContent>
            </Sheet>

            <AlertDialog
                open={!!deleteTarget}
                onOpenChange={(o) => !o && setDeleteTarget(null)}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this enquiry?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={remove}
                            className="bg-destructive hover:bg-destructive/90"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

function Row({ icon: Icon, label, value }) {
    return (
        <div className="flex items-start gap-3">
            <Icon className="w-4 h-4 text-slate-400 mt-1" />
            <div className="min-w-0 flex-1">
                <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-bold">
                    {label}
                </div>
                <div className="text-sm text-slate-900 mt-0.5 break-words">{value}</div>
            </div>
        </div>
    );
}
